import React from 'react';
import { SvgIconProps, Box } from '@mui/material';
import { getIconByName } from 'components/MuiIconPicker';
import { HelpOutline } from '@mui/icons-material';

interface MuiIconDisplayProps extends SvgIconProps {
  iconName?: string;
  fallback?: React.ComponentType<SvgIconProps>;
  showBackground?: boolean;
  backgroundColor?: string;
  size?: number;
}

/**
 * Component to display a MUI icon by its name
 * Shows a fallback icon if the icon name is invalid or not found
 * Optionally displays with a colored circular background
 */
const MuiIconDisplay: React.FC<MuiIconDisplayProps> = ({
  iconName,
  fallback: FallbackIcon = HelpOutline,
  showBackground = false,
  backgroundColor,
  size,
  ...props
}) => {
  if (!iconName) {
    const icon = <FallbackIcon {...props} />;
    return showBackground ? (
      <Box
        sx={{
          width: size || 40,
          height: size || 40,
          borderRadius: '50%',
          backgroundColor: backgroundColor || '#ccc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {React.cloneElement(icon, { sx: { ...props.sx, color: 'white' } })}
      </Box>
    ) : icon;
  }

  const IconComponent = getIconByName(iconName);

  if (!IconComponent) {
    const fallbackIcon = <FallbackIcon {...props} />;
    return showBackground ? (
      <Box
        sx={{
          width: size || 40,
          height: size || 40,
          borderRadius: '50%',
          backgroundColor: backgroundColor || '#ccc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {React.cloneElement(fallbackIcon, { sx: { ...props.sx, color: 'white' } })}
      </Box>
    ) : fallbackIcon;
  }

  const icon = <IconComponent {...props} />;
  
  if (showBackground) {
    return (
      <Box
        sx={{
          width: size || 40,
          height: size || 40,
          borderRadius: '50%',
          backgroundColor: backgroundColor || (props.sx as any)?.color || '#ccc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {React.cloneElement(icon, { sx: { ...props.sx, color: 'white' } })}
      </Box>
    );
  }

  return icon;
};

export default MuiIconDisplay;
